import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import FormatEditor from './FormatEditor';

const UNASSIGNED = '(Unassigned)';

function EditableLabelCell({ value, disabled, saving, placeholder, onCommit }) {
  const [local, setLocal] = useState(value || '');
  useEffect(() => { setLocal(value || ''); }, [value]);
  if (disabled) {
    return <span style={{ fontSize: 13 }}>{value || <span style={{ color: '#adb5bd' }}>—</span>}</span>;
  }
  return (
    <input
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        const next = local.trim();
        const prev = (value || '').trim();
        if (next !== prev) onCommit(next || null);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      placeholder={placeholder || '—'}
      disabled={saving}
      style={{
        width: '100%', minWidth: 90, padding: '4px 6px', fontSize: 12,
        border: '1px solid #ced4da', borderRadius: 4, fontFamily: 'inherit',
      }}
    />
  );
}

const FormatBuilderPanel = ({ instance, accounts, onFormatsChanged, routePrefix = '/pdf-extractor/builder' }) => {
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dupSuccess, setDupSuccess] = useState(null);
  const [editingFormat, setEditingFormat] = useState(null);
  const [editCanEdit, setEditCanEdit] = useState(true);
  const [editCanEditLabels, setEditCanEditLabels] = useState(true);
  const [editCanEditRegions, setEditCanEditRegions] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFirm, setNewFirm] = useState('');
  const [newClient, setNewClient] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [archiveConfirm, setArchiveConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveFilter, setArchiveFilter] = useState('active');
  const [groupBy, setGroupBy] = useState(null); // null | 'engineering_firm' | 'client'
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [savingCell, setSavingCell] = useState(null);

  const isV2 = routePrefix.includes('/v2/');

  const fetchFormats = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs = isV2 ? `?archived=${archiveFilter}` : '';
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${routePrefix}/formats${qs}`);
      if (res.data.success) setFormats(res.data.data.formats || []);
    } catch (err) { setError('Failed to load formats'); }
    finally { setLoading(false); }
  }, [instance, accounts, routePrefix, archiveFilter, isV2]);

  useEffect(() => { fetchFormats(); }, [fetchFormats]);

  const groups = useMemo(() => {
    if (!groupBy) return null;
    const map = new Map();
    for (const f of formats) {
      const raw = groupBy === 'client' ? f.client : f.engineering_firm;
      const key = (raw || '').trim() || UNASSIGNED;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    return Array.from(map.entries())
      .sort((a, b) => {
        if (a[0] === UNASSIGNED) return 1;
        if (b[0] === UNASSIGNED) return -1;
        return a[0].localeCompare(b[0]);
      })
      .map(([name, rows]) => ({ name, rows, count: rows.length }));
  }, [formats, groupBy]);

  useEffect(() => {
    if (!groups) { setExpandedGroups(new Set()); return; }
    setExpandedGroups(new Set(groups.map((g) => g.name)));
  }, [groups]);

  const toggleGroup = (name) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const setGroupMode = (mode) => {
    setGroupBy((prev) => (prev === mode ? null : mode));
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const body = {
        name: newName.trim(),
        description: newDesc.trim(),
        engineeringFirm: newFirm.trim() || null,
        client: newClient.trim() || null,
      };
      await authenticatedApiCall(instance, accounts, 'POST', `${routePrefix}/formats`, body);
      setShowCreate(false); setNewName(''); setNewFirm(''); setNewClient(''); setNewDesc('');
      fetchFormats(); if (onFormatsChanged) onFormatsChanged();
    } catch { setError('Failed to create format'); }
    finally { setCreating(false); }
  };

  const saveLabels = async (f, patch) => {
    if (!isV2) return;
    setSavingCell(`${f.id}-${Object.keys(patch)[0]}`);
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `${routePrefix}/formats/${f.id}/labels`, patch);
      fetchFormats(); if (onFormatsChanged) onFormatsChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSavingCell(null); }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${routePrefix}/formats/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      fetchFormats(); if (onFormatsChanged) onFormatsChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete format');
      setDeleteConfirm(null);
    } finally { setDeleting(false); }
  };

  const handleArchiveConfirm = async () => {
    if (!archiveConfirm) return;
    setArchiving(true);
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `${routePrefix}/formats/${archiveConfirm.id}/archive`);
      setArchiveConfirm(null);
      fetchFormats(); if (onFormatsChanged) onFormatsChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to archive format');
      setArchiveConfirm(null);
    } finally { setArchiving(false); }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `${routePrefix}/formats/${id}/duplicate`);
      if (!res.data?.success) {
        setError(res.data?.details || res.data?.error || 'Failed to duplicate format');
        return;
      }
      fetchFormats();
      if (onFormatsChanged) onFormatsChanged();
      const newName = res.data?.data?.name;
      setError(null);
      if (newName) {
        // Brief success via the same banner slot (green styling below)
        setDupSuccess(`Created “${newName}”. Open Edit on the copy to customize it.`);
        setTimeout(() => setDupSuccess(null), 5000);
      }
    } catch (err) {
      setError(err.response?.data?.details || err.response?.data?.error || 'Failed to duplicate format');
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `${routePrefix}/formats/${id}/toggle-active`);
      fetchFormats(); if (onFormatsChanged) onFormatsChanged();
    } catch (err) { setError(err.response?.data?.error || 'Failed to toggle format'); }
  };

  const handleToggleLock = async (f) => {
    const action = f.is_locked ? 'unlock' : 'lock';
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `${routePrefix}/formats/${f.id}/${action}`);
      fetchFormats(); if (onFormatsChanged) onFormatsChanged();
    } catch (err) { setError(err.response?.data?.error || `Failed to ${action} format`); }
  };

  const handleRestore = async (f) => {
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `${routePrefix}/formats/${f.id}/restore`);
      fetchFormats(); if (onFormatsChanged) onFormatsChanged();
    } catch (err) { setError(err.response?.data?.error || 'Failed to restore format'); }
  };

  const handleEdit = async (id) => {
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${routePrefix}/formats/${id}`);
      if (res.data.success) {
        setEditingFormat(res.data.data);
        setEditCanEdit(res.data.data.canEdit !== false);
        setEditCanEditLabels(res.data.data.canEditLabels !== false);
        setEditCanEditRegions(
          res.data.data.canEditRegions != null
            ? !!res.data.data.canEditRegions
            : res.data.data.canEdit !== false
        );
      }
    } catch { setError('Failed to load format'); }
  };

  const handleEditorClose = () => {
    setEditingFormat(null); fetchFormats();
    if (onFormatsChanged) onFormatsChanged();
  };

  if (editingFormat) {
    return (
      <FormatEditor
        formatData={editingFormat}
        canEdit={editCanEdit}
        canEditLabels={editCanEditLabels}
        canEditRegions={editCanEditRegions}
        instance={instance}
        accounts={accounts}
        onClose={handleEditorClose}
        routePrefix={routePrefix}
      />
    );
  }

  const filterBtn = (key, label) => (
    <button
      key={key}
      onClick={() => setArchiveFilter(key)}
      style={{
        padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        border: '1px solid #ced4da',
        backgroundColor: archiveFilter === key ? '#212529' : '#fff',
        color: archiveFilter === key ? '#fff' : '#495057',
        borderRadius: key === 'active' ? '4px 0 0 4px' : key === 'all' ? '0 4px 4px 0' : 0,
        marginLeft: key === 'active' ? 0 : -1,
      }}
    >{label}</button>
  );

  const colCount = 9;

  const renderFormatRow = (f) => {
    const isLocked = !!f.is_locked;
    const canEdit = f.canEdit !== false && !f.is_builtin;
    const canEditLabels = f.canEditLabels !== false;
    const canDelete = f.canDelete !== undefined ? f.canDelete : false;
    const canLock = !!f.canLock;
    const canArchive = f.canArchive !== undefined ? f.canArchive : (canEdit && !f.is_builtin);
    const isArchived = !!f.is_archived || !!f.archived_at;
    return (
      <tr key={f.id} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: '#fff' }}>
        <td style={tdStyle}>
          <EditableLabelCell
            value={f.engineering_firm}
            disabled={!canEditLabels}
            saving={savingCell === `${f.id}-engineeringFirm`}
            onCommit={(v) => saveLabels(f, { engineeringFirm: v })}
          />
        </td>
        <td style={tdStyle}>
          <EditableLabelCell
            value={f.client}
            disabled={!canEditLabels}
            saving={savingCell === `${f.id}-client`}
            onCommit={(v) => saveLabels(f, { client: v })}
          />
        </td>
        <td style={tdStyle}>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{f.name}</div>
          {f.description && <div style={{ fontSize: 11, color: '#6c757d', marginTop: 1, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.description}</div>}
        </td>
        <td style={tdStyle}>
          {f.is_builtin ? (
            <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 500, backgroundColor: '#cfe2ff', color: '#084298' }}>Built-in</span>
          ) : isArchived ? (
            <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 500, backgroundColor: '#e9ecef', color: '#495057' }}>Archived</span>
          ) : (
            <span style={{
              display: 'inline-block', padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 500,
              backgroundColor: f.is_active ? '#d1e7dd' : '#f8d7da', color: f.is_active ? '#0f5132' : '#842029',
            }}>{f.is_active ? 'Active' : 'Inactive'}</span>
          )}
          {isLocked && <span style={{ marginLeft: 4, fontSize: 10, color: '#664d03' }}>Locked</span>}
        </td>
        <td style={tdStyle}>{f.attachments_count || 0}</td>
        <td style={tdStyle}>{f.regions_count}</td>
        <td style={tdStyle}>{f.columns_count}</td>
        <td style={tdStyle}>
          <span style={{ fontSize: 12, color: '#495057' }}>
            {f.created_by_email ? f.created_by_email.split('@')[0] : (f.is_builtin ? 'system' : '—')}
          </span>
        </td>
        <td style={{ ...tdStyle, textAlign: 'right' }}>
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button onClick={() => handleEdit(f.id)} style={{ ...actionBtn, fontWeight: 600, borderColor: '#0d6efd', color: '#0d6efd' }}>
              {canEdit ? 'Edit' : 'View'}
            </button>
            <button
              onClick={() => handleDuplicate(f.id)}
              title={f.is_builtin ? 'Create an editable custom copy of this built-in format' : 'Duplicate this format'}
              style={{ ...actionBtn, ...(f.is_builtin ? { fontWeight: 600, borderColor: '#0d6efd', color: '#0d6efd' } : {}) }}
            >
              {f.is_builtin ? 'Copy to edit' : 'Copy'}
            </button>
            {isV2 && canArchive && !f.is_builtin && (
              isArchived
                ? <button onClick={() => handleRestore(f)} style={{ ...actionBtn, fontWeight: 600, backgroundColor: '#e7f1ff', borderColor: '#0d6efd', color: '#0d6efd' }}>Restore</button>
                : <button onClick={() => setArchiveConfirm(f)} style={{ ...actionBtn, fontWeight: 600, backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#664d03' }}>Archive</button>
            )}
            {canEdit && !isArchived && (
              <button onClick={() => handleToggleActive(f.id)} style={actionBtn}>{f.is_active ? 'Disable' : 'Enable'}</button>
            )}
            {canLock && (
              <button onClick={() => handleToggleLock(f)} style={actionBtn}>{isLocked ? 'Unlock' : 'Lock'}</button>
            )}
            {canDelete && (
              <button onClick={() => setDeleteConfirm(f)} style={{ ...actionBtn, color: '#dc3545' }}>Delete</button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const tableHead = (
    <thead>
      <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
        <th style={thStyle}>Engineering Firm</th>
        <th style={thStyle}>Client</th>
        <th style={thStyle}>Format</th>
        <th style={thStyle}>Status</th>
        <th style={thStyle}>Samples</th>
        <th style={thStyle}>Regions</th>
        <th style={thStyle}>Columns</th>
        <th style={thStyle}>Owner</th>
        <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
      </tr>
    </thead>
  );

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          {isV2 && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={groupBy === 'engineering_firm'}
                  onChange={() => setGroupMode('engineering_firm')}
                />
                Group by engineering firm
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={groupBy === 'client'}
                  onChange={() => setGroupMode('client')}
                />
                Group by client
              </label>
              {groupBy && (
                <>
                  <button style={btnSecondary} onClick={() => setExpandedGroups(new Set((groups || []).map((g) => g.name)))}>Expand all</button>
                  <button style={btnSecondary} onClick={() => setExpandedGroups(new Set())}>Collapse all</button>
                </>
              )}
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {isV2 && (
            <div style={{ display: 'flex' }}>
              {filterBtn('active', 'Active')}
              {filterBtn('archived', 'Archived')}
              {filterBtn('all', 'All')}
            </div>
          )}
          <button onClick={() => setShowCreate(true)} style={btnPrimary}>+ New Format</button>
        </div>
      </div>

      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6c757d', lineHeight: 1.45 }}>
        Edit <strong>Engineering Firm</strong> and <strong>Client</strong> inline in the table (tab or click away to save).
        Use the checkboxes above to collapse the list by firm or by client — same idea as the Safety Training database.
      </p>

      {showCreate && (
        <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: 16, marginBottom: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Create New Format</h3>
          <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={labelStyle}>Format name *</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Format name" style={inputStyle} autoFocus />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={labelStyle}>Engineering firm</label>
              <input value={newFirm} onChange={e => setNewFirm(e.target.value)} placeholder="Optional" style={inputStyle} />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={labelStyle}>Client</label>
              <input value={newClient} onChange={e => setNewClient(e.target.value)} placeholder="Optional" style={inputStyle} />
            </div>
            <div style={{ flex: 2, minWidth: 180 }}>
              <label style={labelStyle}>Description</label>
              <input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Optional" style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleCreate} disabled={!newName.trim() || creating} style={{ ...btnPrimary, opacity: !newName.trim() || creating ? 0.6 : 1 }}>
              {creating ? 'Creating...' : 'Create'}
            </button>
            <button onClick={() => { setShowCreate(false); setNewName(''); setNewFirm(''); setNewClient(''); setNewDesc(''); }} style={btnSecondary}>Cancel</button>
          </div>
        </div>
      )}

      {error && <div style={{ padding: '8px 12px', backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 6, color: '#842029', marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {dupSuccess && <div style={{ padding: '8px 12px', backgroundColor: '#d1e7dd', border: '1px solid #badbcc', borderRadius: 6, color: '#0f5132', marginBottom: 12, fontSize: 13 }}>{dupSuccess}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#6c757d', fontSize: 13 }}>Loading formats...</div>
      ) : formats.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 24, color: '#6c757d', fontSize: 13 }}>No formats in this view.</div>
      ) : (
        <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            {tableHead}
            <tbody>
              {groupBy && groups ? groups.map((g) => {
                const open = expandedGroups.has(g.name);
                return (
                  <React.Fragment key={g.name}>
                    <tr
                      onClick={() => toggleGroup(g.name)}
                      style={{ backgroundColor: '#f1f5f9', cursor: 'pointer', borderBottom: '1px solid #e2e8f0' }}
                    >
                      <td colSpan={colCount} style={{ ...tdStyle, fontWeight: 700, padding: '9px 12px' }}>
                        <span style={{ display: 'inline-block', width: 16 }}>{open ? '▾' : '▸'}</span>
                        {g.name}
                        <span style={{ marginLeft: 10, fontWeight: 500, color: '#64748b', fontSize: 12 }}>
                          {g.count} format{g.count === 1 ? '' : 's'}
                        </span>
                      </td>
                    </tr>
                    {open && g.rows.map(renderFormatRow)}
                  </React.Fragment>
                );
              }) : formats.map(renderFormatRow)}
            </tbody>
          </table>
        </div>
      )}

      {archiveConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => !archiving && setArchiveConfirm(null)}>
          <div style={{ backgroundColor: '#fff', borderRadius: 10, width: 420, padding: 28 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>Archive format?</h3>
            <p style={{ fontSize: 14, margin: '0 0 16px' }}>Hide <strong>{archiveConfirm.name}</strong> from Active. Restore anytime from the Archived filter.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setArchiveConfirm(null)} style={btnSecondary}>Cancel</button>
              <button onClick={handleArchiveConfirm} disabled={archiving} style={{ ...btnPrimary, backgroundColor: '#ffc107', borderColor: '#ffc107', color: '#212529' }}>
                {archiving ? 'Archiving...' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => !deleting && setDeleteConfirm(null)}>
          <div style={{ backgroundColor: '#fff', borderRadius: 10, width: 420, padding: 28 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>Delete permanently?</h3>
            <p style={{ fontSize: 14, margin: '0 0 16px' }}>Delete <strong>{deleteConfirm.name}</strong>? Prefer Archive if you might need it later.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={btnSecondary}>Cancel</button>
              <button onClick={handleDeleteConfirm} disabled={deleting} style={{ ...btnPrimary, backgroundColor: '#dc3545', borderColor: '#dc3545' }}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const inputStyle = { width: '100%', padding: '6px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13, fontFamily: 'inherit' };
const labelStyle = { display: 'block', marginBottom: 3, fontSize: 12, fontWeight: 500 };
const btnPrimary = { padding: '6px 14px', backgroundColor: '#0d6efd', color: '#fff', border: '1px solid #0d6efd', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer' };
const btnSecondary = { padding: '6px 14px', backgroundColor: '#fff', color: '#6c757d', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer' };
const thStyle = { padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6', textAlign: 'left' };
const tdStyle = { padding: '6px 10px', fontSize: 13, verticalAlign: 'middle' };
const actionBtn = { padding: '4px 10px', fontSize: 11, border: '1px solid #ced4da', borderRadius: 4, backgroundColor: '#fff', cursor: 'pointer', color: '#495057' };

export default FormatBuilderPanel;